import React, { useState, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFMCGProducts } from '../hooks/useFMCGProducts';

interface FMCGProductFieldProps {
  productName: string;
  onProductNameChange: (value: string) => void;
}

export const FMCGProductField: React.FC<FMCGProductFieldProps> = ({
  productName,
  onProductNameChange,
}) => {
  const [open, setOpen] = useState(false);
  const { products, isLoading } = useFMCGProducts();

  const filteredProducts = useMemo(() => {
    if (!productName) return products;
    return products.filter(p => 
      p.name.toLowerCase().includes(productName.toLowerCase())
    );
  }, [products, productName]);

  return (
    <div className="space-y-2">
      <Label>Product Name *</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            {productName || "Search or select product..."}
            {isLoading ? (
              <Loader2 className="ml-2 h-4 w-4 animate-spin" />
            ) : (
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput 
              placeholder="Search products..." 
              value={productName}
              onValueChange={onProductNameChange}
            />
            <CommandList>
              <CommandEmpty>
                <div className="p-2 text-sm text-muted-foreground">
                  No product found. Using "{productName}" as custom entry.
                </div>
              </CommandEmpty>
              <CommandGroup>
                {filteredProducts.map((product) => (
                  <CommandItem
                    key={product.id}
                    value={product.name}
                    onSelect={() => {
                      onProductNameChange(product.name);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        productName === product.name ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {product.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
